<template>
  <div class="dashboard-admin">
    <!-- Header -->
    <b-card class="mb-3">
      <div class="d-flex justify-content-between align-items-center flex-wrap">
        <div>
          <h3 class="mb-0">
            📊 Dashboard Administrativo
          </h3>
          <p class="text-muted mb-0">
            Visão geral do sistema
          </p>
        </div>
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <!-- Filtro de Loja -->
          <div class="d-flex align-items-center mr-2">
            <label
              class="mb-0 mr-2 text-nowrap"
              style="font-weight: 500;"
            >
              <feather-icon
                icon="StoreIcon"
                size="16"
                class="mr-50"
              />
              Loja:
            </label>
            <b-form-select
              v-model="filtros.store_id"
              :options="storeOptionsWithAll"
              style="min-width: 200px;"
              :disabled="carregando || carregandoLojas"
            >
              <template #first>
                <b-form-select-option :value="null">
                  Todas as Lojas
                </b-form-select-option>
              </template>
            </b-form-select>
          </div>

          <!-- Filtro de Data Inicial -->
          <b-form-input
            v-model="filtros.start_date"
            type="date"
            placeholder="Data inicial"
            style="max-width: 150px;"
            class="mr-2"
            :disabled="carregando"
          />

          <!-- Filtro de Data Final -->
          <b-form-input
            v-model="filtros.end_date"
            type="date"
            placeholder="Data final"
            style="max-width: 150px;"
            class="mr-2"
            :disabled="carregando"
          />

          <!-- Botão Aplicar -->
          <b-button
            variant="primary"
            :disabled="carregando"
            @click="aplicarFiltros"
          >
            <feather-icon
              icon="FilterIcon"
              class="mr-50"
            />
            Aplicar Filtros
          </b-button>

          <!-- Botão Limpar -->
          <b-button
            variant="secondary"
            :disabled="carregando"
            @click="limparFiltros"
          >
            <feather-icon
              icon="XIcon"
              class="mr-50"
            />
            Limpar
          </b-button>
        </div>
      </div>
    </b-card>

    <!-- Loading -->
    <div
      v-if="carregando"
      class="text-center py-5"
    >
      <b-spinner variant="primary" />
      <p class="mt-2">
        Carregando métricas...
      </p>
    </div>

    <!-- Erro -->
    <b-alert
      v-if="erro"
      variant="danger"
      show
      dismissible
      @dismissed="erro = null"
    >
      {{ erro }}
    </b-alert>

    <!-- Métricas -->
    <div v-if="!carregando && dados">
      <!-- Período Selecionado -->
      <b-card class="mb-3">
        <div class="d-flex align-items-center flex-wrap">
          <feather-icon
            icon="CalendarIcon"
            class="mr-2"
          />
          <div>
            <strong>Período:</strong>
            {{ dados.periodo.start_date }} até {{ dados.periodo.end_date }}
            <span class="ml-3">
              | <strong>Loja:</strong>
              <b-badge
                :variant="filtros.store_id ? 'primary' : 'secondary'"
                class="ml-1"
              >
                {{ nomeLojaSelecionada }}
              </b-badge>
            </span>
          </div>
        </div>
      </b-card>

      <!-- CAMPANHAS -->
      <b-card class="mb-3">
        <b-card-title>
          <feather-icon
            icon="LayoutIcon"
            class="mr-2"
          />
          Campanhas
        </b-card-title>
        <b-row>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-primary">
                <feather-icon
                  icon="PlusCircleIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ dados.campanhas.total_criadas || 0 }}
                </h4>
                <p class="metric-label">
                  Total Criadas
                </p>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-success">
                <feather-icon
                  icon="SendIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ dados.campanhas.enviadas || 0 }}
                </h4>
                <p class="metric-label">
                  Enviadas
                </p>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-warning">
                <feather-icon
                  icon="ClockIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ dados.campanhas.agendadas || 0 }}
                </h4>
                <p class="metric-label">
                  Agendadas
                </p>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-danger">
                <feather-icon
                  icon="AlertCircleIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ dados.campanhas.com_erro || 0 }}
                </h4>
                <p class="metric-label">
                  Com Erro
                </p>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-info">
                <feather-icon
                  icon="CheckCircleIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ dados.campanhas.finalizadas || 0 }}
                </h4>
                <p class="metric-label">
                  Finalizadas
                </p>
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Gráfico de Pizza: Campanhas por Status -->
        <b-row
          v-if="dados.campanhas.por_status"
          class="mt-4"
        >
          <b-col
            cols="12"
            md="6"
          >
            <b-card>
              <b-card-title>Distribuição de Campanhas por Status</b-card-title>
              <chartjs-component-doughnut-chart
                :height="300"
                :data="chartDataCampanhasPorStatus"
                :options="chartOptionsPie"
              />
            </b-card>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <b-card>
              <b-card-title>Resumo de Campanhas</b-card-title>
              <chartjs-component-bar-chart
                :height="300"
                :data="chartDataCampanhasResumo"
                :options="chartOptionsBar"
              />
            </b-card>
          </b-col>
        </b-row>
      </b-card>

      <!-- MENSAGENS -->
      <b-card class="mb-3">
        <b-card-title>
          <feather-icon
            icon="MessageSquareIcon"
            class="mr-2"
          />
          Mensagens
        </b-card-title>
        <b-row>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-primary">
                <feather-icon
                  icon="MessageCircleIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ formatarNumero(dados.mensagens.total) }}
                </h4>
                <p class="metric-label">
                  Total
                </p>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-success">
                <feather-icon
                  icon="SendIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ formatarNumero(dados.mensagens.enviadas) }}
                </h4>
                <p class="metric-label">
                  Enviadas
                </p>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-info">
                <feather-icon
                  icon="CheckIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ formatarNumero(dados.mensagens.entregues) }}
                </h4>
                <p class="metric-label">
                  Entregues
                </p>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-warning">
                <feather-icon
                  icon="EyeIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ formatarNumero(dados.mensagens.lidas) }}
                </h4>
                <p class="metric-label">
                  Lidas
                </p>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-danger">
                <feather-icon
                  icon="XCircleIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ formatarNumero(dados.mensagens.falharam) }}
                </h4>
                <p class="metric-label">
                  Falharam
                </p>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-success">
                <feather-icon
                  icon="TrendingUpIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ dados.mensagens.taxa_entrega || 0 }}%
                </h4>
                <p class="metric-label">
                  Taxa de Entrega
                </p>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-info">
                <feather-icon
                  icon="TrendingUpIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ dados.mensagens.taxa_leitura || 0 }}%
                </h4>
                <p class="metric-label">
                  Taxa de Leitura
                </p>
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Gráfico de Barras: Status das Mensagens -->
        <b-row class="mt-4">
          <b-col cols="12">
            <b-card>
              <b-card-title>Status das Mensagens</b-card-title>
              <chartjs-component-horizontal-bar-chart
                :height="300"
                :data="chartDataMensagens"
                :options="chartOptionsHorizontalBar"
              />
            </b-card>
          </b-col>
        </b-row>

        <!-- Gráfico de Linha: Taxas de Entrega e Leitura -->
        <b-row class="mt-3">
          <b-col cols="12">
            <b-card>
              <b-card-title>Taxas de Entrega e Leitura</b-card-title>
              <chartjs-component-line-chart
                :height="300"
                :data="chartDataTaxasMensagens"
                :options="chartOptionsLine"
              />
            </b-card>
          </b-col>
        </b-row>
      </b-card>

      <!-- AUDIÊNCIAS -->
      <b-card class="mb-3">
        <b-card-title>
          <feather-icon
            icon="UsersIcon"
            class="mr-2"
          />
          Audiências
        </b-card-title>

        <!-- Alerta de Inconsistência -->
        <b-alert
          v-if="dados && dados.audiencias && verificarInconsistenciaAudiencias"
          variant="warning"
          show
          class="mb-3"
        >
          <strong>Atenção:</strong> Os números de status podem não corresponder ao total de audiências criadas devido a diferentes períodos de filtro ou agregação de dados.
        </b-alert>

        <b-row>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-primary">
                <feather-icon
                  icon="UserPlusIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ formatarNumero(dados.audiencias.total_criadas) }}
                </h4>
                <p class="metric-label">
                  Total Criadas
                </p>
                <small class="text-muted d-block mt-1">Total de audiências criadas no período</small>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-warning">
                <feather-icon
                  icon="LoaderIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ formatarNumero(dados.audiencias.em_processamento) }}
                </h4>
                <p class="metric-label">
                  Em Processamento
                </p>
                <small class="text-muted d-block mt-1">Audiências com status "processing"</small>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-success">
                <feather-icon
                  icon="CheckCircleIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ formatarNumero(dados.audiencias.sucesso) }}
                </h4>
                <p class="metric-label">
                  Sucesso
                </p>
                <small class="text-muted d-block mt-1">Audiências com status "success"</small>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-danger">
                <feather-icon
                  icon="AlertCircleIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ formatarNumero(dados.audiencias.com_erro) }}
                </h4>
                <p class="metric-label">
                  Com Erro
                </p>
                <small class="text-muted d-block mt-1">Audiências com status "error"</small>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-info">
                <feather-icon
                  icon="UsersIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ formatarNumero(dados.audiencias.total_leads) }}
                </h4>
                <p class="metric-label">
                  Total de Leads
                </p>
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Gráfico de Pizza: Status das Audiências -->
        <b-row class="mt-4">
          <b-col
            cols="12"
            md="6"
          >
            <b-card>
              <b-card-title>Status das Audiências</b-card-title>
              <chartjs-component-pie-chart
                :height="300"
                :data="chartDataAudiencias"
                :options="chartOptionsPie"
              />
            </b-card>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <b-card>
              <b-card-title>Distribuição de Leads</b-card-title>
              <chartjs-component-doughnut-chart
                :height="300"
                :data="chartDataLeads"
                :options="chartOptionsPie"
              />
            </b-card>
          </b-col>
        </b-row>
      </b-card>

      <!-- CONVERSÕES E VENDAS -->
      <b-card class="mb-3">
        <b-card-title>
          <feather-icon
            icon="ShoppingCartIcon"
            class="mr-2"
          />
          Conversões e Vendas
        </b-card-title>
        <b-row>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-success">
                <feather-icon
                  icon="ShoppingBagIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ formatarNumero(dados.conversoes.total) }}
                </h4>
                <p class="metric-label">
                  Total de Conversões
                </p>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-info">
                <feather-icon
                  icon="UserCheckIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ formatarNumero(dados.conversoes.leads_unicos) }}
                </h4>
                <p class="metric-label">
                  Leads Únicos Convertidos
                </p>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-primary">
                <feather-icon
                  icon="TrendingUpIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ dados.conversoes.taxa_conversao || 0 }}%
                </h4>
                <p class="metric-label">
                  Taxa de Conversão
                </p>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-success">
                <feather-icon
                  icon="DollarSignIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  R$ {{ formatarMoeda(dados.conversoes.valor_total_campanhas || 0) }}
                </h4>
                <p class="metric-label">
                  Valor Total
                </p>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-info">
                <feather-icon
                  icon="ZapIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ dados.conversoes.vendas_diretas.total || 0 }}
                </h4>
                <p class="metric-label">
                  Vendas Diretas
                </p>
                <small class="text-muted">R$ {{ formatarMoeda(dados.conversoes.vendas_diretas.valor_total || 0) }}</small>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-warning">
                <feather-icon
                  icon="TagIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ dados.conversoes.vendas_por_incentivo.total || 0 }}
                </h4>
                <p class="metric-label">
                  Vendas por Incentivo
                </p>
                <small class="text-muted">R$ {{ formatarMoeda(dados.conversoes.vendas_por_incentivo.valor_total || 0) }}</small>
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Gráfico de Barras: Conversões e Vendas -->
        <b-row class="mt-4">
          <b-col
            cols="12"
            md="6"
          >
            <b-card>
              <b-card-title>Comparação de Conversões</b-card-title>
              <chartjs-component-bar-chart
                :height="300"
                :data="chartDataConversoes"
                :options="chartOptionsBar"
              />
            </b-card>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <b-card>
              <b-card-title>Valores de Vendas</b-card-title>
              <chartjs-component-bar-chart
                :height="300"
                :data="chartDataVendas"
                :options="chartOptionsBar"
              />
            </b-card>
          </b-col>
        </b-row>
      </b-card>

      <!-- PERFORMANCE E ROI -->
      <b-card class="mb-3">
        <b-card-title>
          <feather-icon
            icon="TrendingUpIcon"
            class="mr-2"
          />
          Performance e ROI
        </b-card-title>
        <b-row>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-danger">
                <feather-icon
                  icon="DollarSignIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  R$ {{ formatarMoeda(dados.performance.custo_estimado || 0) }}
                </h4>
                <p class="metric-label">
                  Custo Estimado
                </p>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-success">
                <feather-icon
                  icon="DollarSignIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  R$ {{ formatarMoeda(dados.performance.receita_total || 0) }}
                </h4>
                <p class="metric-label">
                  Receita Total
                </p>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div
                class="metric-icon"
                :class="dados.performance.lucro >= 0 ? 'bg-success' : 'bg-danger'"
              >
                <feather-icon
                  icon="DollarSignIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  R$ {{ formatarMoeda(dados.performance.lucro || 0) }}
                </h4>
                <p class="metric-label">
                  Lucro
                </p>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div
                class="metric-icon"
                :class="dados.performance.roi_percentual >= 0 ? 'bg-success' : 'bg-danger'"
              >
                <feather-icon
                  icon="TrendingUpIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ dados.performance.roi_percentual || 0 }}%
                </h4>
                <p class="metric-label">
                  ROI Percentual
                </p>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-info">
                <feather-icon
                  icon="ShoppingCartIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  R$ {{ formatarMoeda(dados.performance.ticket_medio || 0) }}
                </h4>
                <p class="metric-label">
                  Ticket Médio
                </p>
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Gráfico de Linha: Performance Financeira -->
        <b-row class="mt-4">
          <b-col cols="12">
            <b-card>
              <b-card-title>Performance Financeira</b-card-title>
              <chartjs-component-line-chart
                :height="300"
                :data="chartDataPerformance"
                :options="chartOptionsLine"
              />
            </b-card>
          </b-col>
        </b-row>
      </b-card>

      <!-- RECOMENDAÇÕES -->
      <b-card
        v-if="dados.recomendacoes"
        class="mb-3"
      >
        <b-card-title>
          <feather-icon
            icon="PackageIcon"
            class="mr-2"
          />
          Recomendações
        </b-card-title>
        <b-row>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-primary">
                <feather-icon
                  icon="LayoutIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ dados.recomendacoes.total_campanhas || 0 }}
                </h4>
                <p class="metric-label">
                  Total de Campanhas
                </p>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-info">
                <feather-icon
                  icon="SendIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ formatarNumero(dados.recomendacoes.total_recomendacoes_enviadas) }}
                </h4>
                <p class="metric-label">
                  Recomendações Enviadas
                </p>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-warning">
                <feather-icon
                  icon="MousePointerIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ formatarNumero(dados.recomendacoes.total_cliques) }}
                </h4>
                <p class="metric-label">
                  Total de Cliques
                </p>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-success">
                <feather-icon
                  icon="ShoppingBagIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ dados.recomendacoes.total_conversoes || 0 }}
                </h4>
                <p class="metric-label">
                  Total de Conversões
                </p>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-success">
                <feather-icon
                  icon="DollarSignIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  R$ {{ formatarMoeda(dados.recomendacoes.valor_total || 0) }}
                </h4>
                <p class="metric-label">
                  Valor Total
                </p>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-primary">
                <feather-icon
                  icon="TrendingUpIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ dados.recomendacoes.taxa_cliques || 0 }}%
                </h4>
                <p class="metric-label">
                  Taxa de Cliques
                </p>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-3"
          >
            <div class="metric-card">
              <div class="metric-icon bg-info">
                <feather-icon
                  icon="TrendingUpIcon"
                  size="24"
                />
              </div>
              <div class="metric-content">
                <h4 class="metric-value">
                  {{ dados.recomendacoes.taxa_conversao || 0 }}%
                </h4>
                <p class="metric-label">
                  Taxa de Conversão
                </p>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BCardTitle,
  BRow,
  BCol,
  BButton,
  BFormSelect,
  BFormSelectOption,
  BFormInput,
  BSpinner,
  BAlert,
  BBadge,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import ChartjsComponentPieChart from '@/views/dashboard/notification/charts-components/ChartjsComponentPieChart.vue'
import ChartjsComponentDoughnutChart from '@/views/dashboard/notification/charts-components/ChartjsComponentDoughnutChart.vue'
import ChartjsComponentBarChart from '@/views/dashboard/notification/charts-components/ChartjsComponentBarChart.vue'
import ChartjsComponentHorizontalBarChart from '@/views/dashboard/notification/charts-components/ChartjsComponentHorizontalBarChart.vue'
import ChartjsComponentLineChart from '@/views/dashboard/notification/charts-components/ChartjsComponentLineChart.vue'
import { buscarDashboardAdmin } from '@/functions/basics/campaign-oficial'
import { getAllStoresForAdmin } from '@/functions/basics/stores'
import { getUserData } from '@/auth/utils'

export default {
  name: 'DashboardAdmin',
  components: {
    BCard,
    BCardTitle,
    BRow,
    BCol,
    BButton,
    BFormSelect,
    BFormSelectOption,
    BFormInput,
    BSpinner,
    BAlert,
    BBadge,
    FeatherIcon,
    ChartjsComponentPieChart,
    ChartjsComponentDoughnutChart,
    ChartjsComponentBarChart,
    ChartjsComponentHorizontalBarChart,
    ChartjsComponentLineChart,
  },
  data() {
    return {
      carregando: false,
      carregandoLojas: false,
      erro: null,
      dados: null,
      stores: [],
      filtros: {
        start_date: null,
        end_date: null,
        store_id: null,
      },
    }
  },
  computed: {
    storeOptions() {
      return this.stores.map(store => ({
        value: store.id,
        text: store.name || store.nome || `Loja ${store.id}`,
      }))
    },
    storeOptionsWithAll() {
      return this.stores.map(store => ({
        value: store.id,
        text: store.name || store.nome || `Loja ${store.id}`,
      }))
    },
    nomeLojaSelecionada() {
      if (!this.filtros.store_id) {
        return 'Todas as Lojas'
      }
      const loja = this.stores.find(s => s.id === this.filtros.store_id)
      return loja ? (loja.name || loja.nome || `Loja ${loja.id}`) : 'Todas as Lojas'
    },
    // Gráfico: Campanhas por Status (Pizza)
    chartDataCampanhasPorStatus() {
      if (!this.dados || !this.dados.campanhas.por_status) {
        return { labels: [], datasets: [] }
      }
      const statusLabels = {
        scheduled: 'Agendadas',
        running: 'Em Execução',
        completed: 'Concluídas',
        paused: 'Pausadas',
        cancelled: 'Canceladas',
        error: 'Com Erro',
      }
      const labels = Object.keys(this.dados.campanhas.por_status).map(key => statusLabels[key] || key)
      const data = Object.values(this.dados.campanhas.por_status)
      return {
        labels,
        datasets: [{
          data,
          backgroundColor: [
            '#00cfe8', // info
            '#ff9f43', // warning
            '#28c76f', // success
            '#7367f0', // primary
            '#ea5455', // danger
            '#82868b', // secondary
          ],
        }],
      }
    },
    // Gráfico: Resumo de Campanhas (Barras)
    chartDataCampanhasResumo() {
      if (!this.dados) return { labels: [], datasets: [] }
      return {
        labels: ['Criadas', 'Enviadas', 'Agendadas', 'Com Erro', 'Finalizadas'],
        datasets: [{
          label: 'Quantidade',
          data: [
            this.dados.campanhas.total_criadas || 0,
            this.dados.campanhas.enviadas || 0,
            this.dados.campanhas.agendadas || 0,
            this.dados.campanhas.com_erro || 0,
            this.dados.campanhas.finalizadas || 0,
          ],
          backgroundColor: ['#7367f0', '#28c76f', '#ff9f43', '#ea5455', '#00cfe8'],
        }],
      }
    },
    // Gráfico: Status das Mensagens (Barras Horizontal)
    chartDataMensagens() {
      if (!this.dados) return { labels: [], datasets: [] }
      return {
        labels: ['Enviadas', 'Entregues', 'Lidas', 'Falharam'],
        datasets: [{
          label: 'Quantidade',
          data: [
            this.dados.mensagens.enviadas || 0,
            this.dados.mensagens.entregues || 0,
            this.dados.mensagens.lidas || 0,
            this.dados.mensagens.falharam || 0,
          ],
          backgroundColor: ['#28c76f', '#00cfe8', '#ff9f43', '#ea5455'],
        }],
      }
    },
    // Gráfico: Taxas de Mensagens (Linha)
    chartDataTaxasMensagens() {
      if (!this.dados) return { labels: [], datasets: [] }
      return {
        labels: ['Taxa de Entrega', 'Taxa de Leitura'],
        datasets: [{
          label: 'Percentual (%)',
          data: [
            parseFloat(this.dados.mensagens.taxa_entrega || 0),
            parseFloat(this.dados.mensagens.taxa_leitura || 0),
          ],
          borderColor: '#7367f0',
          backgroundColor: 'rgba(115, 103, 240, 0.1)',
          fill: true,
          tension: 0.4,
        }],
      }
    },
    // Gráfico: Status das Audiências (Pizza)
    chartDataAudiencias() {
      if (!this.dados) return { labels: [], datasets: [] }
      return {
        labels: ['Em Processamento', 'Sucesso', 'Com Erro'],
        datasets: [{
          data: [
            this.dados.audiencias.em_processamento || 0,
            this.dados.audiencias.sucesso || 0,
            this.dados.audiencias.com_erro || 0,
          ],
          backgroundColor: ['#ff9f43', '#28c76f', '#ea5455'],
        }],
      }
    },
    // Gráfico: Distribuição de Leads (Rosca)
    chartDataLeads() {
      if (!this.dados) return { labels: [], datasets: [] }
      const totalLeads = this.dados.audiencias.total_leads || 0
      const totalCriadas = this.dados.audiencias.total_criadas || 0
      return {
        labels: ['Leads em Audiências', 'Audiências Criadas'],
        datasets: [{
          data: [totalLeads, totalCriadas],
          backgroundColor: ['#7367f0', '#00cfe8'],
        }],
      }
    },
    // Gráfico: Conversões (Barras)
    chartDataConversoes() {
      if (!this.dados) return { labels: [], datasets: [] }
      return {
        labels: ['Total', 'Leads Únicos', 'Vendas Diretas', 'Vendas por Incentivo'],
        datasets: [{
          label: 'Quantidade',
          data: [
            this.dados.conversoes.total || 0,
            this.dados.conversoes.leads_unicos || 0,
            this.dados.conversoes.vendas_diretas.total || 0,
            this.dados.conversoes.vendas_por_incentivo.total || 0,
          ],
          backgroundColor: ['#7367f0', '#28c76f', '#00cfe8', '#ff9f43'],
        }],
      }
    },
    // Gráfico: Valores de Vendas (Barras)
    chartDataVendas() {
      if (!this.dados) return { labels: [], datasets: [] }
      return {
        labels: ['Valor Total', 'Vendas Diretas', 'Vendas por Incentivo'],
        datasets: [{
          label: 'Valor (R$)',
          data: [
            this.dados.conversoes.valor_total_campanhas || 0,
            this.dados.conversoes.vendas_diretas.valor_total || 0,
            this.dados.conversoes.vendas_por_incentivo.valor_total || 0,
          ],
          backgroundColor: ['#28c76f', '#00cfe8', '#ff9f43'],
        }],
      }
    },
    // Gráfico: Performance Financeira (Linha)
    chartDataPerformance() {
      if (!this.dados) return { labels: [], datasets: [] }
      return {
        labels: ['Custo', 'Receita', 'Lucro'],
        datasets: [
          {
            label: 'Valor (R$)',
            data: [
              this.dados.performance.custo_estimado || 0,
              this.dados.performance.receita_total || 0,
              this.dados.performance.lucro || 0,
            ],
            borderColor: '#7367f0',
            backgroundColor: 'rgba(115, 103, 240, 0.1)',
            fill: true,
            tension: 0.4,
            pointRadius: 5,
            pointHoverRadius: 7,
          },
        ],
      }
    },
    // Opções dos gráficos
    chartOptionsPie() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 15,
          },
        },
        tooltips: {
          callbacks: {
            label: (tooltipItem, data) => {
              const label = data.labels[tooltipItem.index] || ''
              const value = data.datasets[0].data[tooltipItem.index]
              const total = data.datasets[0].data.reduce((a, b) => a + b, 0)
              const percentage = ((value / total) * 100).toFixed(1)
              return `${label}: ${value} (${percentage}%)`
            },
          },
        },
      }
    },
    chartOptionsBar() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true,
            },
          }],
        },
      }
    },
    chartOptionsHorizontalBar() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        scales: {
          xAxes: [{
            ticks: {
              beginAtZero: true,
            },
          }],
        },
      }
    },
    chartOptionsLine() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 15,
          },
        },
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true,
            },
          }],
        },
      }
    },
  },
  async mounted() {
    // Verificar se é administrador
    const userData = getUserData()
    if (userData.role !== 'admin') {
      this.$router.push({ name: 'misc-not-authorized' })
      return
    }

    // Carregar lojas
    await this.carregarLojas()

    // Carregar dados iniciais
    await this.carregarDados()
  },
  methods: {
    async carregarLojas() {
      this.carregandoLojas = true
      try {
        // Buscar todas as lojas do banco de dados (apenas para admins)
        this.stores = await getAllStoresForAdmin()
        console.log(`✅ ${this.stores.length} lojas carregadas para seleção`)
      } catch (error) {
        console.error('❌ Erro ao carregar lojas:', error)
        this.$bvToast.toast(error.message || 'Erro ao carregar lojas', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.carregandoLojas = false
      }
    },
    async carregarDados() {
      this.carregando = true
      this.erro = null

      try {
        const response = await buscarDashboardAdmin(this.filtros)
        if (response.success && response.data) {
          this.dados = response.data
        } else {
          this.erro = 'Erro ao carregar dados do dashboard'
        }
      } catch (error) {
        console.error('Erro ao carregar dashboard:', error)
        this.erro = error.response?.data?.message || 'Erro ao carregar métricas do dashboard'
      } finally {
        this.carregando = false
      }
    },
    aplicarFiltros() {
      this.carregarDados()
    },
    limparFiltros() {
      this.filtros = {
        start_date: null,
        end_date: null,
        store_id: null,
      }
      this.carregarDados()
    },
    formatarMoeda(valor) {
      return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(valor)
    },
    formatarNumero(valor) {
      return new Intl.NumberFormat('pt-BR').format(valor || 0)
    },
  },
}
</script>

<style scoped>
.dashboard-admin {
  padding: 1rem;
}

.metric-card {
  display: flex;
  align-items: center;
  padding: 1.5rem;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
}

.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.metric-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  color: white;
}

.metric-content {
  flex: 1;
}

.metric-value {
  font-size: 1.75rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
  color: #2c3e50;
}

.metric-label {
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0;
}

.bg-primary {
  background-color: #7367f0 !important;
}

.bg-success {
  background-color: #28c76f !important;
}

.bg-warning {
  background-color: #ff9f43 !important;
}

.bg-danger {
  background-color: #ea5455 !important;
}

.bg-info {
  background-color: #00cfe8 !important;
}
</style>
